import AircallWorkspace from 'aircall-everywhere';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
    AircallDialError,
    aircallDebugLog,
    isAircallDebugEnabled,
    reportAircallDialError,
    reportAircallWarning,
} from 'services/aircallDiagnostics';

// The SDK sometimes never invokes the isLoggedIn callback, typically when the
// workspace iframe cannot reach Aircall at all. Without a deadline the dial does
// nothing whatsoever: no call, no error, nothing to report. That silent hang is
// the one failure an agent cannot describe to support, so it gets a timeout.
const LOGIN_CHECK_TIMEOUT_MS = 8000;

export class AirCallPanel {
    constructor() {
        this.defaultCountry = undefined;
        this.callContext = {};
        this.aircallWorkspace = new AircallWorkspace({
            onLogin: (settings) => {
                aircallDebugLog('workspace logged in', settings);
            },
            onLogout: () => {
                aircallDebugLog('workspace logged out');
            },
            domToLoadWorkspace: '#aircall',
            size: 'big',
            debug: isAircallDebugEnabled(),
        });
        this.close();
    }

    /**
     * Region used to expand national numbers to E.164. Numbers already stored
     * with a country prefix do not depend on it.
     */
    setDefaultCountry(country) {
        const normalized = String(country ?? '')
            .trim()
            .toUpperCase();
        this.defaultCountry = /^[A-Z]{2}$/.test(normalized) ? normalized : undefined;
    }

    /** Organization and agent, so a failure report says who it happened to. */
    setCallContext(context) {
        this.callContext = context ?? {};
    }

    show() {
        const phoneContainer = document.querySelector('#aircall-container');
        phoneContainer?.classList.remove('hidden');
    }

    close() {
        const phoneContainer = document.querySelector('#aircall-container');
        phoneContainer?.classList.add('hidden');
    }

    call(phoneNumber) {
        const context = { ...this.callContext };

        if (!this.isAircallIFrameMounted()) {
            this.failDial({ reason: AircallDialError.NOT_MOUNTED, rawPhone: phoneNumber, context });
            return;
        }

        const rawPhone = String(phoneNumber ?? '').trim();
        if (!rawPhone) {
            this.failDial({ reason: AircallDialError.MISSING_NUMBER, rawPhone: phoneNumber, context });
            return;
        }

        const dialedPhone = this.toE164(rawPhone, context);

        // isLoggedIn and send are both callback based and neither is guaranteed
        // to fire, so a single latch keeps the timeout and the real answer from
        // both reporting the same dial.
        let settled = false;
        const loginCheckTimeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            this.failDial({
                reason: AircallDialError.LOGIN_CHECK_TIMEOUT,
                rawPhone,
                dialedPhone,
                context,
                extra: { timeoutMs: LOGIN_CHECK_TIMEOUT_MS },
            });
        }, LOGIN_CHECK_TIMEOUT_MS);

        this.aircallWorkspace.isLoggedIn((isLoggedIn, loginData) => {
            if (settled) return;
            settled = true;
            clearTimeout(loginCheckTimeout);

            // The SDK also answers false when the workspace did not reply within
            // its own 2s limit (no_answer) or never loaded (not_ready), so the
            // code it returns is what says whether the agent was really logged out.
            if (!isLoggedIn) {
                this.failDial({
                    reason: AircallDialError.NOT_LOGGED_IN,
                    rawPhone,
                    dialedPhone,
                    providerData: loginData,
                    context,
                });
                return;
            }

            this.show();
            aircallDebugLog('dialing', { rawPhone, dialedPhone, defaultCountry: this.defaultCountry });

            // Let the browser lay out and paint the workspace iframe before dialing.
            // Sending dial_number in the same frame the panel becomes visible races
            // the iframe's microphone capture and the call drops right after ringing.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.aircallWorkspace.send('dial_number', { phone_number: dialedPhone }, (success, data) => {
                        if (success) {
                            aircallDebugLog('dial accepted by workspace', data);
                            return;
                        }
                        this.failDial({
                            reason: AircallDialError.DIAL_FAILED,
                            rawPhone,
                            dialedPhone,
                            providerData: data,
                            context,
                        });
                    });
                });
            });
        });
    }

    /**
     * Aircall expects E.164. Numbers coming from a CRM often arrive as
     * "0048 601 234 567" or with dashes, which the workspace rejects.
     *
     * A number libphonenumber cannot validate is still dialed rather than
     * blocked: some numbers Aircall accepts today would otherwise stop working,
     * and that would turn an observability change into an outage. The mismatch
     * is reported as a warning so the pattern is visible in Sentry, and Aircall
     * keeps the final say.
     */
    toE164(rawPhone, context) {
        try {
            const parsed = parsePhoneNumberFromString(rawPhone, this.defaultCountry);
            if (parsed?.isValid()) return parsed.number;

            reportAircallWarning({
                reason: AircallDialError.INVALID_NUMBER,
                rawPhone,
                dialedPhone: rawPhone,
                defaultCountry: this.defaultCountry,
                context,
                extra: {
                    parsedCountry: parsed?.country ?? null,
                    couldParse: Boolean(parsed),
                },
            });
        } catch (cause) {
            reportAircallWarning({
                reason: AircallDialError.INVALID_NUMBER,
                rawPhone,
                dialedPhone: rawPhone,
                defaultCountry: this.defaultCountry,
                context,
                cause,
            });
        }
        return rawPhone;
    }

    // Reporting also surfaces the message to the agent, so there is a single
    // path from a failure to what the user is told about it.
    failDial({ reason, rawPhone, dialedPhone, providerData, context, extra, cause }) {
        reportAircallDialError({
            reason,
            rawPhone,
            dialedPhone,
            defaultCountry: this.defaultCountry,
            providerData,
            context: { ...context, ...(extra ?? {}) },
            cause,
        });
    }

    listen(event, payload, callback) {
        this.aircallWorkspace.on(event, (data) => callback(data, event, payload));
    }

    removeListener(eventName) {
        this.aircallWorkspace.removeListener(eventName);
    }

    isLoggedIn(callback) {
        this.aircallWorkspace.isLoggedIn(callback);
    }

    // The SDK injects the iframe with innerHTML, so it is lost whenever React
    // unmounts the container. Without this check a stale panel instance would
    // keep the phone dead until a full page reload.
    isAircallIFrameMounted() {
        return Boolean(document.querySelector('#aircall iframe'));
    }
}
